import React from "react";
import i18next from "i18next";
import { cn } from "../../../../utils/classnames";
import { ArrowCircleDownIcon, ArrowCircleUpIcon } from "../../../icons";
import moment from "moment-timezone";


const terminalMapping = {
  "terminal-1": "Terminal 1",
  "terminal-2": "Terminal 2",
  "terminal-3": "Terminal 3",
  "terminal-1-domestic": "Terminal 1 Domestic",
  "terminal-2-domestic": "Terminal 2 Domestic",
  "terminal-2-international": "Terminal 2 International",
  "terminal-3-domestic": "Terminal 3 Domestic",
  "terminal-3-international": "Terminal 3 International",
}

const vehicleTagMapping = {
  "Toyota.Hiace.Tag": "Large luxury van",
  "Toyota.HiAce.Grand.Cabin.Tag": "Large luxury van",
  "HiAce.Grand.Cabin.Tag": "Large luxury van",
  "Toyota.Granace.Tag": "Luxury van",
  "Toyota.GranAce.Tag": "Luxury van",
  "Toyota.Alphard.Tag": "Luxury van",
  "Mercedes.Benz.V-Class.Tag": "Luxury van",
  "Mercedes.Benz.S-Class.Tag": "Luxury sedan",
  "Toyota.Alphard.WELL.Cabin.Luxe.Tag": "Luxury van",
  "WELL.Cabin.Granluxe.Tag": "Large luxury van",
  "WELL.Cabin.GranLuxe.Tag": "Large luxury van",
  "Toyota.HiAce.Luxury.Cabin.Tag": "Large luxury van",
}

const travelAreaMapping = {
  "neighboringPerfecture": "To the neighboring city/prefecture",
  "withinCity": "Within the city/Prefecture",
  "undecided": "Undecided",
}

const formatVehicleTagName = (vehicleInfo) => {
  if (!vehicleInfo) return "-";

  return vehicleTagMapping[vehicleInfo?.vehicleTypeName.replace(/ /g, ".") + ".Tag"]?.toUpperCase() || "-";
};

// ─── Table sub-components ────────────────────────────────────────────

const SectionTitleRow = ({ title }) => (
  <div className="tw:min-h-[36px] tw:py-2 tw:w-full tw:border-x tw:border-b tw:border-[#e8e8e8] tw:bg-[#f9f9f9] tw:px-5 tw:flex tw:items-center tw:justify-center">
    <div className="tw:text-[#8d8d8d] tw:text-[14px] tw:leading-[20px] tw:font-hanken-grotesk tw:text-center">{title}</div>
  </div>
);

const LabelCell = ({ label, isFirst }) => (
  <div
    className={cn(
      "tw:w-[90px] tw:sm:w-[225px] tw:px-2 tw:sm:px-5 tw:py-[14px] tw:border-l tw:border-b tw:border-[#e8e8e8] tw:bg-white tw:flex tw:items-center",
      isFirst && "tw:rounded-bl-[5px]"
    )}
  >
    <div className="tw:text-[#8d8d8d] tw:text-[11px] tw:sm:text-[14px] tw:leading-[18px] tw:sm:leading-[20px] tw:font-hanken-grotesk">{label}</div>
  </div>
);

const ValueText = ({ text, secondary, muted, bold, secondaryBold = false }) => (
  <div className="tw:flex tw:flex-col tw:gap-[2px] tw:!w-full tw:!h-full tw:!justify-center">
    {text && (
      <div
        className={cn(
          "tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px]",
          muted ? "tw:text-[#8d8d8d] tw:italic" : "tw:text-[#282828]",
          bold ? "tw:font-bold" : "tw:font-normal"
        )}
      >
        {text}
      </div>
    )}
    {secondary && (
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        {(Array.isArray(secondary) ? secondary : [secondary]).map((line, i) => (
          <div key={i} className={cn(
            "tw:font-hanken-grotesk tw:font-normal tw:leading-[18.5px]",
            line === "None" ? "tw:text-[14px] tw:font-[400] tw:text-[#8D8D8D]" : "tw:text-[13px] tw:text-[#7c7b7b]",
            secondaryBold && "tw:font-bold"
          )}>
            {line}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ValuePaxText = ({ text, muted, bold }) => (
  <div className="tw:flex tw:flex-col tw:justify-center tw:gap-[2px] tw:w-full">
    {text && (
      <div className="tw:flex tw:flex-col tw:gap-0.5">
        {(Array.isArray(text) ? text : [text]).map((line, i) => (
          <div
            key={i}
            className={cn(
              "tw:font-hanken-grotesk tw:text-[15px] tw:leading-[22px]",
              muted ? "tw:text-[#8d8d8d] tw:italic" : "tw:text-[#282828]",
              bold ? "tw:font-bold" : "tw:font-normal"
            )}
          >
            {line}
          </div>
        ))}
      </div>
    )}
  </div>
);

const ValueCell = ({ children, changed, muted, isRight, isLast, className }) => (
  <div
    className={cn(
      "tw:flex-1 tw:min-h-[68px] tw:px-2 tw:sm:px-5 tw:py-[14px] tw:border-l tw:border-b tw:border-[#e8e8e8] tw:flex tw:items-center",
      changed ? "tw:bg-[#fff8e8]" : muted ? "tw:bg-[#f3f3f3]" : "tw:bg-white",
      isRight && "tw:border-r",
      isRight && isLast && "tw:rounded-br-[5px]",
      className
    )}
  >
    {children}
  </div>
);

const ComparisonRow = ({ label, before, after, afterChanged, beforeMuted, afterMuted, lastRow }) => (
  <div className="tw:flex tw:w-full">
    <LabelCell label={label} isFirst={lastRow} />
    <ValueCell muted={beforeMuted}>{before}</ValueCell>
    <ValueCell changed={afterChanged} muted={afterMuted} isRight isLast={lastRow}>
      {after}
    </ValueCell>
  </div>
);

const VehicleCell = ({ vehicleType = "-", vehicleClass = "Vehicle", vehicleImage = "-", bold }) => (
  <div className="tw:flex tw:flex-col tw:lg:flex-row tw:items-start tw:lg:items-center tw:justify-between tw:w-full tw:gap-4 tw:lg:gap-0">
    <div className="tw:flex tw:flex-col tw:gap-1.5 tw:w-full tw:lg:w-[307px]">
      <div className="tw:text-[#8d8d8d] tw:text-[8px] tw:leading-[12px] tw:tracking-[3px] tw:uppercase tw:font-semibold tw:font-hanken-grotesk">
        {vehicleClass}
      </div>
      <div className={cn(
        "tw:text-[#282828] tw:text-[15px] tw:leading-[22px] tw:font-hanken-grotesk",
        bold ? "tw:font-bold" : "tw:font-normal"
      )}>
        {vehicleType}
      </div>
    </div>
    <img src={vehicleImage} alt={vehicleType} className="tw:w-full tw:lg:w-[165px] tw:h-[88px] tw:object-contain tw:rounded-[5px] tw:lg:rounded-none" />
  </div>
);

const PriceDetailsCell = ({ details, title, highlight, bold }) => (
  <div className="tw:flex tw:flex-col tw:gap-[5px] tw:w-full">
    {title && (
      <div className={cn("tw:font-hanken-grotesk tw:text-[13px] tw:sm:text-[15px] tw:leading-[20px] tw:sm:leading-[22px] tw:text-[#282828]", bold ? "tw:font-bold" : "tw:font-normal")}>
        {title}
      </div>
    )}
    <div className="tw:flex tw:flex-col tw:gap-1">
      {details.map((item) => (
        <div
          key={item.label}
          className="tw:flex tw:flex-col tw:lg:flex-row tw:items-start tw:lg:items-center tw:justify-between tw:gap-0.5 tw:lg:gap-2"
        >
          <div
            className={cn(
              "tw:font-hanken-grotesk tw:break-all",
              item.label === "None"
                ? "tw:!text-[14px] tw:!font-[400] tw:!text-[#8D8D8D] tw:leading-[20px]"
                : cn(
                  "tw:text-[11px] tw:sm:text-[13px] tw:leading-[16px] tw:sm:leading-[18.5px]",
                  item.type === "discount" ? "tw:text-[#EA4335]" : "tw:text-[#8d8d8d]"
                )
            )}
          >
            {item.label}
          </div>
          {item.value && (
            <div
              className={cn(
                "tw:text-[12px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-hanken-grotesk tw:whitespace-nowrap",
                bold && "tw:font-bold",
                item.type === "discount"
                  ? "tw:text-[#EA4335]"
                  : highlight
                    ? "tw:text-[#282828]"
                    : item.value === "-" || item.value === "0 JPY" || item.value === 0
                      ? "tw:text-[#8d8d8d]"
                      : "tw:text-[#282828]"
              )}
            >
              {item.value === "0 JPY" || item.value === 0 ? "-" : item.value}
            </div>
          )}
        </div>
      ))}
    </div>
  </div>
);

// ─── Helpers ─────────────────────────────────────────────────────────

const DATE_TIME_FORMAT = "MMM. Do, YYYY (ddd) HH:mm";

const getLocationDetail = (loc) => {
  const formattedAddressArr = loc?.formatted_address?.split(",");
  const name = formattedAddressArr?.[0];
  const address = formattedAddressArr?.slice(1).join(",");
  return { name, address };
};

const formatJPY = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value)) || value === 0) return "-";
  return `${Number(value).toLocaleString()} JPY`;
};

const formatDuration = (duration) => {
  if (!duration) return "-";
  const [h = "0", m = "0"] = duration.split(":");
  const totalMinutes = Number(h) * 60 + Number(m);
  const decimalHours = totalMinutes / 60;
  const rounded = Math.ceil(decimalHours * 2) / 2;
  return `${rounded} hours`;
};

const minuteToHoursFormat = (minutes) => {
  if (!minutes) return "-";
  const decimalHours = minutes / 60;
  return Math.ceil(decimalHours * 2) / 2;
};

const formatTransferType = (value, isAirportTransfer) => {
  if (value === "byTheHour") return "By The Hour";
  if (value === "specifiedDestination") return isAirportTransfer ? "Airport Transfer" : "Specified Destination";
  return "-";
};

const formatPaxGroup = (paxList = []) => {
  if (!Array.isArray(paxList)) return "";
  return paxList
    .filter((item) => Number(item?.paxcount || 0) > 0)
    .map((item) => `${item?.paxtype}: ${item?.paxcount}`)
    .join(", ");
};

const formatLuggageGroup = (luggageList = []) => {
  if (!Array.isArray(luggageList)) return "";
  return luggageList
    .filter((item) => Number(item?.luggagecount || 0) > 0)
    .map((item) => {
      const type = String(item?.luggagetype || "").toLowerCase().includes("check") ? "Check-in" : "Cabin";
      return `${type}: ${item?.luggagecount}`;
    })
    .join(", ");
};

const formatVehicleTypeName = (vehicleInfo) => {
  if (!vehicleInfo) return "-";
  return vehicleInfo?.vehicleTypeName || "-";
};

const formatPassengerName = (title, name) => {
  const fullName = `${title || ""} ${name || ""}`.trim();
  return fullName || "-";
};

const formatPassengerPhone = (prefix, phone) => {
  if (!phone) return "-";
  return `(${prefix || "-"}) ${phone}`.trim();
};

const formatMessage = (message) => {
  if (!message || !Array.isArray(message) || !message[0]?.note) return "-";
  return message[0].note;
};

const formatMeetAssist = (meetAssistList = []) => {
  const enT = i18next.getFixedT('en');
  const arrival = meetAssistList.find((item) => item?.meetAssistType === "arrival")?.meetAssistName;
  const departure = meetAssistList.find((item) => item?.meetAssistType === "departure")?.meetAssistName;

  const hasArrival = Boolean(arrival && arrival !== "none");
  const hasDeparture = Boolean(departure && departure !== "none");

  if (!hasArrival && !hasDeparture) {
    return { title: enT("editBooking.BookingReviewPage.none") };
  }

  let text = "";
  if (hasArrival) {
    text += `${enT(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${arrival}`)} (${enT(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.arrival`)})`;
  }
  if (hasDeparture) {
    if (text) text += "\n";
    text += `${enT(`editBooking.BookingReviewPage.additionalInformation.meetAssistType.${departure}`)} (${enT(`editBooking.BookingReviewPage.additionalInformation.meetAssistLoc.departure`)})`;
  }

  return {
    title: text,
  };
};

const getPickupSignText = (honorific, name) => {
  if (!name) return "-";
  if (honorific === "-") return name;
  return `${honorific} ${name}`;
};

const getVehiclePriceDetails = (vehicle) => {
  if (!vehicle) return [];
  const details = [
    { label: "Charter Cost", type: "price", value: formatJPY(vehicle.total) },
    { label: "Late Night Fee", type: "price", value: formatJPY(vehicle.nightPrice) },
  ];
  if (vehicle.discount?.amount > 0) {
    if (vehicle.discount?.campaignEligible) {
      details.push({
        label: `Summer Campaign Discount (${vehicle.discount?.rate}%)`,
        type: "discount",
        value: `-${formatJPY(vehicle.discount?.amount)}`
      });
    } else if (vehicle.discount?.iataEligible) {
      details.push({
        label: `Summer Campaign Discount (${vehicle.discount?.rate}%)`,
        type: "discount",
        value: `-${formatJPY(vehicle.discount?.amount)}`
      });
    }
  }
  details.push(
    { label: "Toll Fee", type: "price", value: formatJPY((vehicle.tollFare || 0) + (vehicle.travelAreaPrice || 0)) },
    { label: "Long Distance Surcharge", type: "price", value: formatJPY(vehicle.longDistanceSurcharge || 0) },
    { label: "Add-On", type: "price", value: formatJPY(vehicle.childSeatPrice) }
  );
  return details;
};

const normalizeVehiclePricing = (vehicle) => {
  if (!vehicle) return null;

  return {
    total: Number(vehicle?.total || 0),
    tollFare: Number(vehicle?.tollFare || 0),
    travelAreaPrice: Number(vehicle?.travelAreaPrice || 0),
    nightPrice: Number(vehicle?.nightPrice || 0),
    longDistanceSurcharge: Number(vehicle?.longDistanceSurcharge || 0),
    childSeatPrice: Number(vehicle?.childSeatPrice || 0),
    baseprice: Number(vehicle?.baseprice || 0),
    hourlyprice: Number(vehicle?.hourlyprice || 0),
    discount: Number(vehicle?.discount?.rate || 0),
    totalDiscount: Number(vehicle?.discount?.amount || 0),
  };
};

const isVehiclePricingChanged = (beforeVehicle, afterVehicle) => (
  JSON.stringify(normalizeVehiclePricing(beforeVehicle)) !== JSON.stringify(normalizeVehiclePricing(afterVehicle))
);

const getVehicleAddonDetails = (vehicle) => {
  const enT = i18next.getFixedT('en');
  if (!vehicle || !Array.isArray(vehicle.childSeat)) return [];

  const getChildSeatTypeLabel = (type, count) => {
    const typeKey = String(type || "").toLowerCase();
    return enT(`editBooking.BookingReviewPage.additionalInformation.childSeatType.${typeKey}`, {
      count,
      defaultValue: `${type || "-"} x${count ?? 0}`,
    });
  };

  const addons = vehicle.childSeat
    .filter((seat) => Number(seat?.childSeatCount || 0) > 0)
    .map((seat) => ({ label: getChildSeatTypeLabel(seat?.childSeatType, seat?.childSeatCount) }));
  return addons.length > 0 ? addons : [{ label: enT("editBooking.BookingReviewPage.none", { defaultValue: "None" }) }];
};

const getChangeData = (changes, key) => {
  const item = changes?.[key] || {};
  return { newValue: item?.new, oldValue: item?.old, changed: Boolean(item?.changed) };
};

const getOldBookingPaymentInfo = (booking, deferredPayment) => {
  if (deferredPayment) {
    if (booking?.paymentStatus !== "CHARGE_PENDING") {
      if (booking?.confirmWithEditedFee) {
        return "Added to monthly invoice with edited fee";
      }
      return "Added to monthly invoice";
    }
  } else {
    if (booking?.paymentStatus !== "CHARGE_PENDING") {
      if (booking?.editedFeeType === "CHARGE_EDITED_FEE") {
        return "Charged with edited fee";
      }
      return "Charged";
    }
  }

  return "Not charged";
};

// ─── Main component ──────────────────────────────────────────────────

const ComparisonTable = ({ data }) => {
  const enT = i18next.getFixedT('en');
  const changes = data?.changesIdentification || {};
  const oldBooking = data?.bookingId || {};
  const calculatedEditBooking = data?.editBooking || {};
  const previousBooking = data?.previousBooking || {};

  const bookingTypeChange = getChangeData(changes, "bookingType");
  const paxChange = getChangeData(changes, "pax");
  const luggageChange = getChangeData(changes, "luggage");
  const pickupLocationChange = getChangeData(changes, "pickupLocation");
  const destinationsChange = getChangeData(changes, "destinations");
  const durationChange = getChangeData(changes, "duration");
  const billedDurationChange = getChangeData(changes, "billedDuration");
  const travelAreaChange = getChangeData(changes, "travelArea");
  const pickupDateTimeChange = getChangeData(changes, "pickupDateTime");
  const customerDropoffDateTimeChange = getChangeData(changes, "customerDropoffDateTime");
  const pickupFlightNoChange = getChangeData(changes, "pickupFlightNo");
  const pickupTerminalChange = getChangeData(changes, "pickupTerminal");
  const pickupSignHonorificChange = getChangeData(changes, "pickupSignHonorific");
  const pickupSignChange = getChangeData(changes, "pickupSign");
  const dropoffFlightNoChange = getChangeData(changes, "dropoffFlightNo");
  const dropoffTerminalChange = getChangeData(changes, "dropoffTerminal");
  const meetAssistChange = getChangeData(changes, "meetAssist");
  const bookingInfoChange = getChangeData(changes, "bookingInfo");
  const specialRequestChange = getChangeData(changes, "message");
  const passengerNameChange = getChangeData(changes, "passengerName");
  const passengerTitleChange = getChangeData(changes, "passengerPronounced");
  const passengerPrefixChange = getChangeData(changes, "passengerPrefixNumber");
  const passengerPhoneChange = getChangeData(changes, "passengerPhoneNumber");
  const totalMeetAssistPriceChange = getChangeData(changes, "totalMeetAssistPrice");
  const totalPriceChange = getChangeData(changes, "totalPrice");

  const beforeVehicles = Array.isArray(bookingInfoChange?.oldValue) ? bookingInfoChange.oldValue : [];
  const afterVehicles = Array.isArray(bookingInfoChange?.newValue) ? bookingInfoChange.newValue : [];
  const maxVehicles = Math.max(beforeVehicles.length, afterVehicles.length, 1);

  const vehiclePairs = (() => {
    const pairs = [];
    const usedAfterIndices = new Set();

    for (let i = 0; i < beforeVehicles.length; i++) {
      const vBefore = beforeVehicles[i];
      const beforeTypeName = vBefore?.vehicleTypeName || "";
      const matchIndex = afterVehicles.findIndex((vAfter, j) => {
        if (usedAfterIndices.has(j)) return false;
        return beforeTypeName && (vAfter?.vehicleTypeName || "") === beforeTypeName;
      });
      if (matchIndex !== -1) {
        usedAfterIndices.add(matchIndex);
        pairs.push({ before: vBefore, after: afterVehicles[matchIndex] });
      } else {
        pairs.push({ before: vBefore, after: null });
      }
    }

    // Second pass: pair unmatched "before" entries with leftover "after" vehicles positionally
    const unmatchedAfter = afterVehicles.filter((_, j) => !usedAfterIndices.has(j));
    let unmatchedIdx = 0;
    for (let i = 0; i < pairs.length; i++) {
      if (pairs[i].after === null && unmatchedIdx < unmatchedAfter.length) {
        pairs[i].after = unmatchedAfter[unmatchedIdx++];
      }
    }
    while (unmatchedIdx < unmatchedAfter.length) {
      pairs.push({ before: null, after: unmatchedAfter[unmatchedIdx++] });
    }

    return pairs;
  })();

  const oldPassengerName = passengerNameChange?.oldValue ?? oldBooking?.passengerName;
  const newPassengerName = passengerNameChange?.newValue ?? calculatedEditBooking?.passengerName;
  const oldPassengerTitle = passengerTitleChange?.oldValue ?? oldBooking?.passengerPronounced;
  const newPassengerTitle = passengerTitleChange?.newValue ?? calculatedEditBooking?.passengerPronounced;
  const oldPassengerPrefix = passengerPrefixChange?.oldValue ?? oldBooking?.passengerPrefixNumber;
  const newPassengerPrefix = passengerPrefixChange?.newValue ?? calculatedEditBooking?.passengerPrefixNumber;
  const oldPassengerPhone = passengerPhoneChange?.oldValue ?? oldBooking?.passengerPhoneNumber;
  const newPassengerPhone = passengerPhoneChange?.newValue ?? calculatedEditBooking?.passengerPhoneNumber;

  const passengerNameChanged = (passengerNameChange?.changed || passengerTitleChange?.changed)
    && (formatPassengerName(oldPassengerTitle, oldPassengerName) !== formatPassengerName(newPassengerTitle, newPassengerName));
  const passengerPhoneChanged = (passengerPhoneChange?.changed || passengerPrefixChange?.changed)
    && (formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone) !== formatPassengerPhone(newPassengerPrefix, newPassengerPhone));

  const beforePrice = Number(totalPriceChange?.oldValue || 0);
  const afterPrice = Number(totalPriceChange?.newValue || 0);
  const diffPrice = afterPrice - beforePrice;

  const oldTravelAreaValue = travelAreaChange?.oldValue ?? oldBooking?.travelArea;
  const newTravelAreaValue = travelAreaChange?.newValue ?? calculatedEditBooking?.travelArea;
  const getTravelAreaLabel = (value) => travelAreaMapping[value] || value || "-";

  const pickupFlightLabelType = (() => {
    const fromAirport = oldBooking.isFromAirport || calculatedEditBooking.isFromAirport;
    const fromStation = oldBooking.isFromStation || calculatedEditBooking.isFromStation;
    if (fromAirport && fromStation) return "both";
    if (fromStation) return "station";
    return "airport";
  })();

  const dropoffFlightLabelType = (() => {
    const toAirport = oldBooking.isToAirport || calculatedEditBooking.isToAirport;
    const toStation = oldBooking.isToStation || calculatedEditBooking.isToStation;
    if (toAirport && toStation) return "both";
    if (toStation) return "station";
    return "airport";
  })();

  const FLIGHT_LABEL = { airport: "Flight No.", station: "Train No.", both: "Flight / Train No." };

  const userDeferredPaymentEligible = Boolean(
    data?.userId?.deferredPaymentEligible
  );

  return (
    <section className="tw:w-full tw:overflow-hidden">
      {/* Header row */}
      <div className="tw:flex tw:w-full">
        <div className="tw:w-[90px] tw:sm:w-[225px] tw:h-[68px] tw:bg-white tw:border-l tw:border-t tw:border-[#e8e8e8] tw:rounded-tl-[5px]" />
        <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center">
          <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">Before</div>
        </div>
        <div className="tw:flex-1 tw:h-[68px] tw:bg-[#282828] tw:border-t tw:border-b tw:border-l tw:border-[#f9f9f9] tw:px-2 tw:sm:px-5 tw:flex tw:items-center tw:rounded-tr-[5px]">
          <div className="tw:text-white tw:text-[13px] tw:sm:text-[15px] tw:leading-[18px] tw:sm:leading-[22px] tw:font-bold">After</div>
        </div>
      </div>

      {/* General Information */}
      <SectionTitleRow title="General Information" />

      <ComparisonRow
        label="Transfer Type"
        before={<ValueText text={formatTransferType(bookingTypeChange?.oldValue, oldBooking.isAirportTransfer)} muted={!bookingTypeChange?.oldValue} />}
        after={<ValueText text={formatTransferType(bookingTypeChange?.newValue, calculatedEditBooking.isAirportTransfer)} bold={bookingTypeChange?.changed} />}
        afterChanged={bookingTypeChange?.changed}
      />
      <ComparisonRow
        label="Pax & Luggage"
        before={<ValuePaxText text={[formatPaxGroup(paxChange?.oldValue), formatLuggageGroup(luggageChange?.oldValue)].filter(Boolean)} />}
        after={<ValuePaxText text={[formatPaxGroup(paxChange?.newValue), formatLuggageGroup(luggageChange?.newValue)].filter(Boolean)} bold={paxChange?.changed || luggageChange?.changed} />}
        afterChanged={paxChange?.changed || luggageChange?.changed}
      />
      <ComparisonRow
        label="Pickup Location"
        before={<ValueText text={getLocationDetail(pickupLocationChange?.oldValue)?.name} secondary={getLocationDetail(pickupLocationChange?.oldValue)?.address} />}
        after={<ValueText text={getLocationDetail(pickupLocationChange?.newValue)?.name} secondary={getLocationDetail(pickupLocationChange?.newValue)?.address} bold={pickupLocationChange?.changed} />}
        afterChanged={pickupLocationChange?.changed}
      />

      {/* Dropoff locations */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Dropoff Location" />
        <div className="tw:flex tw:flex-col tw:flex-1">
          {(() => {
            const oldDests = Array.isArray(destinationsChange?.oldValue) ? destinationsChange.oldValue : [];
            const newDests = Array.isArray(destinationsChange?.newValue) ? destinationsChange.newValue : [];
            const maxRows = Math.max(oldDests.length, newDests.length, 1);

            return Array.from({ length: maxRows }).map((_, i) => {
              const pair = {
                old: oldDests[i] || null,
                new: newDests[i] || null,
              };
              const oldLocation = pair.old ? getLocationDetail(pair.old) : null;
              const newLocation = pair.new ? getLocationDetail(pair.new) : null;
              const rowChanged = destinationsChange?.changed;
              return (
                <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                  <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                    <ValueCell muted={!pair.old} className="tw:w-full">
                      {oldLocation ? <ValueText text={oldLocation.name} secondary={oldLocation.address} /> : null}
                    </ValueCell>
                  </div>
                  <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                    <ValueCell changed={rowChanged && !!pair.new} isRight muted={!pair.new} className="tw:w-full">
                      {newLocation ? <ValueText text={newLocation.name} secondary={newLocation.address} bold={rowChanged} /> : null}
                    </ValueCell>
                  </div>
                </div>
              );
            });
          })()}
        </div>
      </div>

      {/* Duration & Travel Area (by-the-hour only) */}
      {(bookingTypeChange?.oldValue === "byTheHour" || bookingTypeChange?.newValue === "byTheHour") && (
        <>
          <ComparisonRow
            label="Usage Duration"
            before={<ValueText text={formatDuration(durationChange?.oldValue)} secondary={bookingTypeChange?.oldValue === "byTheHour" ? `Billed Duration: ${minuteToHoursFormat(billedDurationChange?.oldValue)} hours` : ""} muted={!durationChange?.oldValue} />}
            after={<ValueText text={formatDuration(durationChange?.newValue)} secondary={bookingTypeChange?.newValue === "byTheHour" ? `Billed Duration: ${minuteToHoursFormat(billedDurationChange?.newValue)} hours` : ""} bold={durationChange?.changed} />}
            afterChanged={durationChange?.changed}
            beforeMuted={!durationChange?.oldValue}
          />
          <ComparisonRow
            label="Travel Area"
            before={<ValueText text={bookingTypeChange?.oldValue === "byTheHour" ? getTravelAreaLabel(oldTravelAreaValue) : "-"} muted={!oldTravelAreaValue} />}
            after={<ValueText text={bookingTypeChange?.newValue === "byTheHour" ? getTravelAreaLabel(newTravelAreaValue) : "-"} bold={travelAreaChange?.changed} />}
            afterChanged={travelAreaChange?.changed}
            beforeMuted={!oldTravelAreaValue}
          />
        </>
      )}

      <ComparisonRow
        label="Pickup Time"
        before={<ValueText text={moment(pickupDateTimeChange?.oldValue).locale("en").format(DATE_TIME_FORMAT)} />}
        after={<ValueText text={moment(pickupDateTimeChange?.newValue).locale("en").format(DATE_TIME_FORMAT)} bold={pickupDateTimeChange?.changed} />}
        afterChanged={pickupDateTimeChange?.changed}
      />
      <ComparisonRow
        label="Est. Dropoff Time"
        before={<ValueText text={customerDropoffDateTimeChange?.oldValue ? moment(customerDropoffDateTimeChange.oldValue).locale("en").format(DATE_TIME_FORMAT) : "-"} muted={!customerDropoffDateTimeChange?.oldValue} />}
        after={<ValueText text={customerDropoffDateTimeChange?.newValue ? moment(customerDropoffDateTimeChange.newValue).locale("en").format(DATE_TIME_FORMAT) : "-"} bold={customerDropoffDateTimeChange?.changed} />}
        afterChanged={customerDropoffDateTimeChange?.changed}
        beforeMuted={!customerDropoffDateTimeChange?.oldValue}
      />

      {/* Vehicle Selection */}
      <SectionTitleRow title="Vehicle Selection" />

      {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
        const vehicleChanged = (formatVehicleTypeName(vehicleBefore) !== formatVehicleTypeName(vehicleAfter));
        return (
          <ComparisonRow
            key={i}
            label={`Vehicle${vehiclePairs.length > 1 ? ` ${i + 1}` : ""}`}
            before={vehicleBefore ? <VehicleCell vehicleClass={formatVehicleTagName(vehicleBefore)} vehicleType={formatVehicleTypeName(vehicleBefore)} vehicleImage={vehicleBefore?.vehicleTypeID?.images?.png} /> : "-"}
            after={vehicleAfter ? <VehicleCell vehicleClass={formatVehicleTagName(vehicleAfter)} vehicleType={formatVehicleTypeName(vehicleAfter)} vehicleImage={vehicleAfter?.vehicleTypeID?.images?.png} bold={vehicleChanged} /> : "-"}
            afterChanged={vehicleChanged}
            beforeMuted={!vehicleBefore}
            afterMuted={!vehicleAfter}
          />
        );
      })}

      {/* Passenger Information */}
      <SectionTitleRow title="Passenger Information" />

      <ComparisonRow
        label="Passenger Name"
        before={<ValueText text={formatPassengerName(oldPassengerTitle, oldPassengerName)} muted={!oldPassengerName && !oldPassengerTitle} />}
        after={<ValueText text={formatPassengerName(newPassengerTitle, newPassengerName)} bold={passengerNameChanged} />}
        afterChanged={passengerNameChanged}
        beforeMuted={!oldPassengerName && !oldPassengerTitle}
      />
      <ComparisonRow
        label="Passenger Phone Number"
        before={<ValueText text={formatPassengerPhone(oldPassengerPrefix, oldPassengerPhone)} muted={!oldPassengerPhone} />}
        after={<ValueText text={formatPassengerPhone(newPassengerPrefix, newPassengerPhone)} bold={passengerPhoneChanged} />}
        afterChanged={passengerPhoneChanged}
        beforeMuted={!oldPassengerPhone}
      />

      {/* Additional Information */}
      <SectionTitleRow title="Additional Information" />

      {((oldBooking.isAirportTransfer || calculatedEditBooking.isAirportTransfer) || (oldBooking.isStationTransfer || calculatedEditBooking.isStationTransfer)) && (
        <>
          {(oldBooking.isFromAirport || calculatedEditBooking.isFromAirport || oldBooking.isFromStation || calculatedEditBooking.isFromStation) && (
            <>
              <ComparisonRow
                label={`Pickup ${FLIGHT_LABEL[pickupFlightLabelType]}`}
                before={<ValueText text={pickupFlightNoChange?.oldValue || "-"} muted={!pickupFlightNoChange?.oldValue} />}
                after={<ValueText text={pickupFlightNoChange?.newValue || "-"} bold={pickupFlightNoChange?.changed} />}
                afterChanged={pickupFlightNoChange?.changed}
                beforeMuted={!pickupFlightNoChange?.oldValue}
              />
              {(oldBooking.isFromAirport || calculatedEditBooking.isFromAirport) && (
                <>
                  <ComparisonRow
                    label="Pickup Terminal"
                    before={<ValueText text={terminalMapping[pickupTerminalChange?.oldValue] || "-"} muted={!pickupTerminalChange?.oldValue} />}
                    after={<ValueText text={terminalMapping[pickupTerminalChange?.newValue] || "-"} bold={pickupTerminalChange?.changed} />}
                    afterChanged={pickupTerminalChange?.changed}
                    beforeMuted={!pickupTerminalChange?.oldValue}
                  />
                  <ComparisonRow
                    label="Pickup Sign"
                    before={<ValueText text={getPickupSignText(pickupSignHonorificChange?.oldValue, pickupSignChange?.oldValue)} muted={!pickupSignChange?.oldValue} />}
                    after={<ValueText text={getPickupSignText(pickupSignHonorificChange?.newValue, pickupSignChange?.newValue)} bold={pickupSignChange?.changed || pickupSignHonorificChange?.changed} />}
                    afterChanged={pickupSignChange?.changed || pickupSignHonorificChange?.changed}
                    beforeMuted={!pickupSignChange?.oldValue}
                  />
                </>
              )}
            </>
          )}

          {(oldBooking.isToAirport || calculatedEditBooking.isToAirport || oldBooking.isToStation || calculatedEditBooking.isToStation) && (
            <>
              <ComparisonRow
                label={`Dropoff ${FLIGHT_LABEL[dropoffFlightLabelType]}`}
                before={<ValueText text={dropoffFlightNoChange?.oldValue || "-"} muted={!dropoffFlightNoChange?.oldValue} />}
                after={<ValueText text={dropoffFlightNoChange?.newValue || "-"} bold={dropoffFlightNoChange?.changed} />}
                afterChanged={dropoffFlightNoChange?.changed}
                beforeMuted={!dropoffFlightNoChange?.oldValue}
              />
              {(oldBooking.isToAirport || calculatedEditBooking.isToAirport) && (
                <ComparisonRow
                  label="Dropoff Terminal"
                  before={<ValueText text={terminalMapping[dropoffTerminalChange?.oldValue] || "-"} muted={!dropoffTerminalChange?.oldValue} />}
                  after={<ValueText text={terminalMapping[dropoffTerminalChange?.newValue] || "-"} bold={dropoffTerminalChange?.changed} />}
                  afterChanged={dropoffTerminalChange?.changed}
                  beforeMuted={!dropoffTerminalChange?.oldValue}
                />
              )}
            </>
          )}

          <ComparisonRow
            label="Meet & Assist Service"
            before={<ValueText text={formatMeetAssist(meetAssistChange?.oldValue).title} />}
            after={<ValueText text={formatMeetAssist(meetAssistChange?.newValue).title} bold={meetAssistChange?.changed} />}
            afterChanged={meetAssistChange?.changed}
          />
        </>
      )}

      {/* Add-on Services */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Add-On Services" />
        <div className="tw:flex tw:flex-col tw:flex-1">
          {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
            const showVehicleLabel = vehiclePairs.length > 1;
            const addonChanged = (() => {
              if (!vehicleBefore && !vehicleAfter) return false;
              if (!vehicleBefore || !vehicleAfter) return true;
              const beforeSeats = JSON.stringify((vehicleBefore.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
              const afterSeats = JSON.stringify((vehicleAfter.childSeat || []).map(s => ({ type: s.childSeatType, count: s.childSeatCount })));
              return beforeSeats !== afterSeats;
            })();
            return (
              <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                  {showVehicleLabel && (
                    <div className={cn(
                      "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                      vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                    )}>
                      <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                        {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                      </div>
                    </div>
                  )}
                  <div className="tw:flex-1 tw:flex">
                    <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                      {vehicleBefore && <PriceDetailsCell title={enT("editBooking.BookingReviewPage.additionalInformation.childSeat", { defaultValue: "Child Seat" })} details={getVehicleAddonDetails(vehicleBefore)} />}
                    </ValueCell>
                  </div>
                </div>
                <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1">
                  {showVehicleLabel && (
                    <div className={cn(
                      "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                      vehicleAfter ? (addonChanged ? "tw:bg-[#fff8e8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                    )}>
                      <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                        {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                      </div>
                    </div>
                  )}
                  <div className="tw:flex-1 tw:flex">
                    <ValueCell changed={addonChanged && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                      {vehicleAfter && <PriceDetailsCell title={enT("editBooking.BookingReviewPage.additionalInformation.childSeat", { defaultValue: "Child Seat" })} details={getVehicleAddonDetails(vehicleAfter)} highlight bold={addonChanged} />}
                    </ValueCell>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <ComparisonRow
        label="Special Request"
        before={<ValueText text={formatMessage(specialRequestChange?.oldValue) || "-"} />}
        after={<ValueText text={formatMessage(specialRequestChange?.newValue) || "-"} bold={specialRequestChange?.changed} />}
        afterChanged={specialRequestChange?.changed}
      />

      {/* Pricing */}
      <SectionTitleRow title="Pricing" />

      <div className="tw:flex tw:w-full">
        <LabelCell label="Detail" />
        <div className="tw:flex tw:flex-col tw:flex-1">
          {vehiclePairs.map(({ before: vehicleBefore, after: vehicleAfter }, i) => {
            const showVehicleLabel = vehiclePairs.length > 1;
            const changed = isVehiclePricingChanged(vehicleBefore, vehicleAfter);
            return (
              <div key={i} className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
                <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                  {showVehicleLabel && (
                    <div className={cn(
                      "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                      vehicleBefore ? "tw:bg-[#f9f9f9]" : "tw:bg-[#f3f3f3]"
                    )}>
                      <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                        {vehicleBefore ? formatVehicleTypeName(vehicleBefore) : "-"}
                      </div>
                    </div>
                  )}
                  <div className="tw:flex-1 tw:flex">
                    <ValueCell muted={!vehicleBefore} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                      {vehicleBefore && <PriceDetailsCell details={getVehiclePriceDetails(vehicleBefore)} />}
                    </ValueCell>
                  </div>
                </div>
                <div className="tw:flex tw:flex-col tw:lg:flex-row tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
                  {showVehicleLabel && (
                    <div className={cn(
                      "tw:w-full tw:lg:w-[30%] tw:flex tw:items-center tw:justify-center tw:p-2 tw:border-b tw:lg:border-r tw:border-l tw:border-[#e8e8e8]",
                      vehicleAfter ? (changed ? "tw:bg-[#fff8e8]" : "tw:bg-[#f9f9f9]") : "tw:bg-[#f3f3f3]"
                    )}>
                      <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[15px] tw:font-normal tw:text-center tw:break-word">
                        {vehicleAfter ? formatVehicleTypeName(vehicleAfter) : "-"}
                      </div>
                    </div>
                  )}
                  <div className="tw:flex-1 tw:flex">
                    <ValueCell changed={changed && !!vehicleAfter} isRight muted={!vehicleAfter} className={cn("tw:w-full", showVehicleLabel && "tw:border-l-0 tw:lg:border-l")}>
                      {vehicleAfter && <PriceDetailsCell details={getVehiclePriceDetails(vehicleAfter)} highlight bold={changed} />}
                    </ValueCell>
                  </div>
                </div>
              </div>
            );
          })}
          <div className="tw:flex tw:flex-row tw:w-full tw:items-stretch">
            <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
              <ValueCell>
                <PriceDetailsCell details={[{ label: "Meet & Assist Service", value: formatJPY(totalMeetAssistPriceChange?.oldValue) }]} />
              </ValueCell>
            </div>
            <div className="tw:flex tw:flex-1 tw:border-l tw:border-b tw:border-[#e8e8e8]">
              <ValueCell changed={totalMeetAssistPriceChange?.changed} isRight>
                <PriceDetailsCell highlight details={[{ label: "Meet & Assist Service", value: formatJPY(totalMeetAssistPriceChange?.newValue) }]} />
              </ValueCell>
            </div>
          </div>
        </div>
      </div>

      {/* Total Price */}
      <div className="tw:flex tw:w-full">
        <LabelCell label="Total Price" isFirst />
        <ValueCell>
          <PriceDetailsCell highlight details={[{ label: getOldBookingPaymentInfo(previousBooking, userDeferredPaymentEligible), value: formatJPY(totalPriceChange?.oldValue) }]} />
        </ValueCell>
        <ValueCell changed={totalPriceChange?.changed} isRight isLast>
          <div className="tw:flex tw:flex-col tw:items-end tw:gap-[3px] tw:w-full">
            <div className="tw:text-[#282828] tw:text-[14px] tw:sm:text-[16px] tw:leading-[20px] tw:sm:leading-[22px] tw:font-medium">
              {formatJPY(totalPriceChange?.newValue)}
            </div>
            {totalPriceChange?.changed && (
              <div className="tw:flex tw:items-center tw:gap-1 tw:text-[13px] tw:leading-[18.5px] tw:font-bold tw:text-[#ea4335]">
                <span>{diffPrice > 0 ? "+" : ""}{formatJPY(diffPrice)}</span>
                {diffPrice > 0 ? <ArrowCircleUpIcon color="#EA4335" size={15} /> : <ArrowCircleDownIcon color="#EA4335" size={15} />}
              </div>
            )}
          </div>
        </ValueCell>
      </div>
    </section>
  );
};

export default ComparisonTable;
